@if (!isLoading) {
  <div class="seg-site-dialog">
    <div class="dialog-header">
      <div>
        <h2>{{ 'Assign SEGs to Site' | translate }}: {{ selectedSiteName }}</h2>
        @if (activeStep === 1) {
          <p class="subtitle">{{ 'Step 1 of 2 - Choose which SEGs apply to which org levels' | translate }}</p>
        }
        @if (activeStep === 2) {
          <p class="subtitle">{{ 'Step 2 of 2 — Enter workforce & shift details (per-cell autosave)' | translate }}</p>
        }
        <div class="stepper">
          <div class="step" [class.active]="activeStep === 1" [class.done]="activeStep === 2" [class.clickable]="activeStep === 2" (click)="activeStep === 2 && backToScope()">
            <span class="num">1</span>
            <span class="label">{{ 'Scope' | translate }}</span>
          </div>
          <span class="connector" [class.active]="activeStep === 2"></span>
          <div class="step" [class.active]="activeStep === 2">
            <span class="num">2</span>
            <span class="label">{{ 'Workforce & shift data' | translate }}</span>
          </div>
        </div>
      </div>
      <button type="button" class="close-btn" (click)="cancel()" aria-label="Close">&times;</button>
    </div>
    @if (activeStep === 1) {
      <div class="dialog-body">
        <p class="scope-hint">{{ 'Toggle a cell to assign that SEG to that org level. Use the row or column checkbox to select everywhere at once.' | translate }}</p>
        @if (isMatrixReady) {
          <div class="org-level-type-filter">
            <span class="filter-label">{{ 'Org level type' | translate }}:</span>
            <button type="button" [class.active]="orgLevelTypeFilter === 'all'" (click)="setOrgLevelTypeFilter('all')">{{ 'All' | translate }}</button>
            <button type="button" [class.active]="orgLevelTypeFilter === 'site'" (click)="setOrgLevelTypeFilter('site')">{{ 'Sites' | translate }}</button>
            <button type="button" [class.active]="orgLevelTypeFilter === 'department'" (click)="setOrgLevelTypeFilter('department')">{{ 'Departments' | translate }}</button>
            <button type="button" [class.active]="orgLevelTypeFilter === 'other'" (click)="setOrgLevelTypeFilter('other')">{{ 'Other' | translate }}</button>
          </div>
        }
        <!-- Show loading progress if matrix is still initializing -->
        @if (!isMatrixReady) {
          <div class="matrix-loading">
            <div class="loading-bar-container">
              <div class="loading-bar" [style.width.%]="matrixInitProgress"></div>
            </div>
            <p>{{ 'Loading matrix data...' | translate }} {{ matrixInitProgress }}%</p>
          </div>
        }
        <!-- Matrix table: single table with a sticky header so header cells and body
        cells share the exact same columns (checkmarks always centre under their header). -->
        @if (isMatrixReady) {
          <div class="matrix-wrap">
            <div class="matrix-viewport">
              <table class="matrix-table" [style.width.px]="matrixTableWidthPx">
                <colgroup>
                <col class="identity-col" />
                @for (orgLevel of matrixOrgLevels; track trackByIndex($index)) {
                  <col
                    [class.d-none]="!orgLevelVisibility[orgLevel.id]"
                    />
                }
              </colgroup>
              <thead>
                <tr>
                  <th class="sticky-col">
                    <label>
                      <input type="checkbox" [checked]="matrixSelectAll" (change)="toggleMatrixSelectAll($event.target.checked)" />
                      {{ 'SEG / Org Level' | translate }}
                    </label>
                  </th>
                  @for (orgLevel of matrixOrgLevels; track trackByIndex($index)) {
                    <th [class.d-none]="!orgLevelVisibility[orgLevel.id]">
                      <label class="org-level-header-label">
                        <input
                          type="checkbox"
                          [checked]="colSelection[orgLevel.id]"
                          (change)="toggleOrgLevelColumn(orgLevel.id, $event.target.checked)"
                          />
                          <span class="org-level-header">{{ orgLevel.name }}</span>
                        </label>
                      </th>
                    }
                  </tr>
                </thead>
                <tbody>
                  @for (rowView of matrixRows; track trackByIndex($index)) {
                    <tr>
                      <th class="sticky-col">
                        <label>
                          <input
                            type="checkbox"
                            [checked]="rowSelection[rowView.seg.id]"
                            (change)="toggleSegRow(rowView.seg.id, $event.target.checked)"
                            />
                            {{ rowView.seg.name }}
                          </label>
                        </th>
                        @for (cell of rowView.cells; track trackByIndex($index)) {
                          <td [class.d-none]="!orgLevelVisibility[cell.orgLevelId]">
                            <label class="cell-checkbox">
                              <input
                                type="checkbox"
                                [checked]="cell.selected"
                                [disabled]="cell.existing"
                                (change)="toggleCell(cell.segId, cell.orgLevelId)"
                                />
                              </label>
                            </td>
                          }
                        </tr>
                      }
                    </tbody>
                  </table>
                </div>
              </div>
            }
            <div class="matrix-summary">
              <span><strong>{{ matrixExistingSelectedCount }}</strong> {{ 'pairs already exist and will be skipped' | translate }}</span>
              <span class="dot">.</span>
              <span><strong>{{ matrixNewSelectedCount }}</strong> {{ 'new pairs selected' | translate }}</span>
            </div>
          </div>
        }
        @if (activeStep === 2) {
          <div class="dialog-body">
            <div class="toolbar">
              <div class="search-box">
                <i class="fa fa-search search-icon" aria-hidden="true"></i>
                <input
                  type="text"
                  [ngModel]="filterText"
                  (ngModelChange)="onFilterChange($event)"
                  [placeholder]="('Filter by SEG or org level' | translate) + '...'"
                  />
                </div>
                <label class="toggle-pill">
                  <input type="checkbox" [(ngModel)]="showErrorsOnly" (ngModelChange)="toggleShowErrorsOnly($event)">
                  {{ 'Show only errors' | translate }}
                </label>
                <button type="button" class="link-jump" [disabled]="filteredEntryRows.length === 0 || !anyErrors" (click)="jumpToFirstError()">
                  {{ 'Jump to first error' | translate }}
                </button>
                <div class="spacer"></div>
              </div>
              <div class="autosave-note" role="status">
                <i class="fas fa-info-circle" aria-hidden="true"></i>
                <span>{{ 'Each cell autosaves as soon as you edit it — there is no separate Save step.' | translate }}</span>
              </div>
              <div class="entry-grid-shell">
                <div class="entry-grid-header-wrap" #entryGridHeaderWrap>
                  <div class="entry-grid-header">
                    <div class="col status-col"></div>
                    <div class="col sticky-col seg-col">
                      <button type="button" class="sort-btn" [class.active]="sortColumn === 'segName'" (click)="setSort('segName')">
                        <span class="col-title">{{ 'SEG' | translate }}</span>
                        @if (sortColumn === 'segName') {
                          <span class="sort-indicator">{{ sortDirection === 'asc' ? '↑' : '↓' }}</span>
                        }
                      </button>
                    </div>
                    <div class="col sticky-col org-level-col">
                      <button type="button" class="sort-btn" [class.active]="sortColumn === 'orgLevelName'" (click)="setSort('orgLevelName')">
                        <span class="col-title">{{ 'Org Level' | translate }}</span>
                        @if (sortColumn === 'orgLevelName') {
                          <span class="sort-indicator">{{ sortDirection === 'asc' ? '↑' : '↓' }}</span>
                        }
                      </button>
                    </div>
                    <div class="col">
                      <button type="button" class="sort-btn" [class.active]="sortColumn === 'shiftId'" (click)="setSort('shiftId')">
                        <span class="col-title">{{ 'Shift' | translate }}</span>
                        @if (sortColumn === 'shiftId') {
                          <span class="sort-indicator">{{ sortDirection === 'asc' ? '↑' : '↓' }}</span>
                        }
                      </button>
                      <button type="button" class="set-all-link" (click)="openApplyAll('shiftId', $event)">{{ 'Set all' | translate }} &#9662;</button>
                    </div>
                    <div class="col">
                      <button type="button" class="sort-btn" [class.active]="sortColumn === 'numberOfWorkers'" (click)="setSort('numberOfWorkers')">
                        <span class="col-title">{{ '# Employees' | translate }}</span>
                        @if (sortColumn === 'numberOfWorkers') {
                          <span class="sort-indicator">{{ sortDirection === 'asc' ? '↑' : '↓' }}</span>
                        }
                      </button>
                      <button type="button" class="set-all-link" (click)="openApplyAll('numberOfWorkers', $event)">{{ 'Set all' | translate }} &#9662;</button>
                    </div>
                    <div class="col">
                      <button type="button" class="sort-btn" [class.active]="sortColumn === 'numberOfContractors'" (click)="setSort('numberOfContractors')">
                        <span class="col-title">{{ '# Contractors' | translate }}</span>
                        @if (sortColumn === 'numberOfContractors') {
                          <span class="sort-indicator">{{ sortDirection === 'asc' ? '↑' : '↓' }}</span>
                        }
                      </button>
                      <button type="button" class="set-all-link" (click)="openApplyAll('numberOfContractors', $event)">{{ 'Set all' | translate }} &#9662;</button>
                    </div>
                    <div class="col oel-col">
                      <button type="button" class="sort-btn" [class.active]="sortColumn === 'shiftLengthHours'" (click)="setSort('shiftLengthHours')">
                        <span class="col-title">
                          <span class="oel-badge" [title]="'Used in OEL shift-correction calculations' | translate">&#9873;</span>
                          {{ 'Shift Length (hrs)' | translate }}
                        </span>
                        @if (sortColumn === 'shiftLengthHours') {
                          <span class="sort-indicator">{{ sortDirection === 'asc' ? '↑' : '↓' }}</span>
                        }
                      </button>
                      <button type="button" class="set-all-link" (click)="openApplyAll('shiftLengthHours', $event)">{{ 'Set all' | translate }} &#9662;</button>
                    </div>
                    <div class="col oel-col">
                      <button type="button" class="sort-btn" [class.active]="sortColumn === 'avgWeeklyHours'" (click)="setSort('avgWeeklyHours')">
                        <span class="col-title">
                          <span class="oel-badge" [title]="'Used in OEL shift-correction calculations' | translate">&#9873;</span>
                          {{ 'Avg Weekly Hrs' | translate }}
                        </span>
                        @if (sortColumn === 'avgWeeklyHours') {
                          <span class="sort-indicator">{{ sortDirection === 'asc' ? '↑' : '↓' }}</span>
                        }
                      </button>
                      <button type="button" class="set-all-link" (click)="openApplyAll('avgWeeklyHours', $event)">{{ 'Set all' | translate }} &#9662;</button>
                    </div>
                    <div class="header-scroll-gutter" #entryGridHeaderGutter></div>
                  </div>
                </div>
                <cdk-virtual-scroll-viewport [itemSize]="entryRowHeight" class="entry-grid-viewport">
                  <div
                    class="entry-row"
                    *cdkVirtualFor="let row of filteredEntryRows; let rowIndex = index; trackBy: trackByIndex"
                    [class.row-alt]="rowIndex % 2 === 1"
                    >
                    <div class="col status-col">
                      @if (row.rowState === 'saving') {
                        <span>...</span>
                      }
                      @if (row.rowState === 'error') {
                        <span>!</span>
                      }
                    </div>
                    <div class="col sticky-col seg-col">
                      <div class="id-text">
                        <strong>{{ row.segName }}</strong>
                        @if (row.isNew) {
                          <span class="new-chip">{{ 'NEW' | translate }}</span>
                        }
                      </div>
                    </div>
                    <div class="col sticky-col org-level-col">
                      <div class="id-text">
                        <span class="org-level-name">{{ row.orgLevelName }}</span>
                      </div>
                    </div>
                    <div class="col">
                      <div class="cell-wrap" [class.error]="row.cellState.shiftId === 'error'">
                        <select
                          [id]="'cell-' + row.key + '-shiftId'"
                          [(ngModel)]="row.shiftId"
                          (focus)="onCellFocus(row, 'shiftId')"
                          (ngModelChange)="onCellValueChange(row, 'shiftId')"
                          (blur)="onCellBlur(row, 'shiftId')"
                          (keydown)="onCellArrowNav($event, rowIndex, 'shiftId')"
                          >
                          <option [ngValue]="null">{{ 'Select shift...' | translate }}</option>
                          @for (shift of shifts; track trackByIndex($index)) {
                            <option [ngValue]="shift.id">{{ shift.name }}</option>
                          }
                        </select>
                        <span class="state" [attr.data-state]="row.cellState.shiftId"></span>
                        @if (row.cellErrors.shiftId) {
                          <div class="error-text">
                            {{ row.cellErrors.shiftId }}
                            <button type="button" class="retry-btn" (click)="retryCell(row, 'shiftId')">{{ 'Retry' | translate }}</button>
                          </div>
                        }
                      </div>
                    </div>
                    <div class="col">
                      <div class="cell-wrap" [class.error]="row.cellState.numberOfWorkers === 'error'">
                        <input
                          [id]="'cell-' + row.key + '-numberOfWorkers'"
                          type="number"
                          [(ngModel)]="row.numberOfWorkers"
                          (focus)="onCellFocus(row, 'numberOfWorkers')"
                          (ngModelChange)="onCellValueChange(row, 'numberOfWorkers')"
                          (blur)="onCellBlur(row, 'numberOfWorkers')"
                          (keydown)="onCellArrowNav($event, rowIndex, 'numberOfWorkers')"
                          />
                          <span class="state" [attr.data-state]="row.cellState.numberOfWorkers"></span>
                          @if (row.cellErrors.numberOfWorkers) {
                            <div class="error-text">
                              {{ row.cellErrors.numberOfWorkers }}
                              <button type="button" class="retry-btn" (click)="retryCell(row, 'numberOfWorkers')">{{ 'Retry' | translate }}</button>
                            </div>
                          }
                        </div>
                      </div>
                      <div class="col">
                        <div class="cell-wrap" [class.error]="row.cellState.numberOfContractors === 'error'">
                          <input
                            [id]="'cell-' + row.key + '-numberOfContractors'"
                            type="number"
                            [(ngModel)]="row.numberOfContractors"
                            (focus)="onCellFocus(row, 'numberOfContractors')"
                            (ngModelChange)="onCellValueChange(row, 'numberOfContractors')"
                            (blur)="onCellBlur(row, 'numberOfContractors')"
                            (keydown)="onCellArrowNav($event, rowIndex, 'numberOfContractors')"
                            />
                            <span class="state" [attr.data-state]="row.cellState.numberOfContractors"></span>
                            @if (row.cellErrors.numberOfContractors) {
                              <div class="error-text">
                                {{ row.cellErrors.numberOfContractors }}
                                <button type="button" class="retry-btn" (click)="retryCell(row, 'numberOfContractors')">{{ 'Retry' | translate }}</button>
                              </div>
                            }
                          </div>
                        </div>
                        <div class="col oel-col">
                          <div class="cell-wrap" [class.error]="row.cellState.shiftLengthHours === 'error'">
                            @if (!row.hasLinkedShift) {
                              <input
                                [id]="'cell-' + row.key + '-shiftLengthHours'"
                                type="number"
                                step="0.1"
                                [(ngModel)]="row.shiftLengthHours"
                                (focus)="onCellFocus(row, 'shiftLengthHours')"
                                (ngModelChange)="onCellValueChange(row, 'shiftLengthHours')"
                                (blur)="onCellBlur(row, 'shiftLengthHours')"
                                (keydown)="onCellArrowNav($event, rowIndex, 'shiftLengthHours')"
                                />
                              } @else {
                                <div class="derived">
                                  <span class="lock-icon" [title]="'read-only when shift is linked' | translate"><i class="fas fa-lock" aria-hidden="true"></i></span>
                                  @if (row.shiftLengthHours !== null) {
                                    {{ row.shiftLengthHours }} <small>{{ 'from' | translate }} {{ row.linkedShiftName }}</small>
                                  } @else {
                                    -
                                  }
                                </div>
                              }
                              <span class="state" [attr.data-state]="row.cellState.shiftLengthHours"></span>
                              @if (row.cellErrors.shiftLengthHours) {
                                <div class="error-text">
                                  {{ row.cellErrors.shiftLengthHours }}
                                  <button type="button" class="retry-btn" (click)="retryCell(row, 'shiftLengthHours')">{{ 'Retry' | translate }}</button>
                                </div>
                              }
                            </div>
                          </div>
                          <div class="col oel-col">
                            <div class="cell-wrap" [class.error]="row.cellState.avgWeeklyHours === 'error'">
                              @if (!row.hasLinkedShift) {
                                <input
                                  [id]="'cell-' + row.key + '-avgWeeklyHours'"
                                  type="number"
                                  step="0.1"
                                  placeholder="e.g. 42.5"
                                  [(ngModel)]="row.avgWeeklyHours"
                                  (focus)="onCellFocus(row, 'avgWeeklyHours')"
                                  (ngModelChange)="onCellValueChange(row, 'avgWeeklyHours')"
                                  (blur)="onCellBlur(row, 'avgWeeklyHours')"
                                  (keydown)="onCellArrowNav($event, rowIndex, 'avgWeeklyHours')"
                                  />
                                } @else {
                                  <div class="derived">
                                    <span class="lock-icon" [title]="'read-only when shift is linked' | translate"><i class="fas fa-lock" aria-hidden="true"></i></span>
                                    @if (row.avgWeeklyHours !== null) {
                                      {{ row.avgWeeklyHours }} <small>{{ 'from' | translate }} {{ row.linkedShiftName }}</small>
                                    } @else {
                                      -
                                    }
                                  </div>
                                }
                                <span class="state" [attr.data-state]="row.cellState.avgWeeklyHours"></span>
                                @if (row.cellErrors.avgWeeklyHours) {
                                  <div class="error-text">
                                    {{ row.cellErrors.avgWeeklyHours }}
                                    <button type="button" class="retry-btn" (click)="retryCell(row, 'avgWeeklyHours')">{{ 'Retry' | translate }}</button>
                                  </div>
                                }
                              </div>
                            </div>
                          </div>
                        </cdk-virtual-scroll-viewport>
                      </div>
                      @if (applyAllColumn) {
                        <div
                          class="apply-all"
                          [style.top.px]="applyAllPosition.top"
                          [style.left.px]="applyAllPosition.left"
                          >
                          <h4>
                            {{ 'Set {{label}} for all rows' | translate: { label: applyAllShortLabels[applyAllColumn] | translate } }}
                          </h4>
                          @switch (applyAllColumn) {
                            @case ('shiftId') {
                              <select #applyAllFocusTarget [(ngModel)]="applyAllValue">
                                <option [ngValue]="null">{{ 'Select shift...' | translate }}</option>
                                @for (shift of shifts; track trackByIndex($index)) {
                                  <option [ngValue]="shift.id">{{ shift.name }}</option>
                                }
                              </select>
                            }
                            @default {
                              <input #applyAllFocusTarget type="number" [(ngModel)]="applyAllValue" />
                            }
                          }
                          @if (applyAllSkippedCount === 0) {
                            <p>
                              {{ 'Applies to' | translate }} {{ applyAllApplicableCount }} {{ 'visible rows' | translate }}.
                            </p>
                          }
                          @if (applyAllSkippedCount > 0) {
                            <p>
                              {{ 'Applies to' | translate }} {{ applyAllApplicableCount }} {{ 'of' | translate }} {{ filteredEntryRows.length }} {{ 'rows' | translate }}.
                              {{ applyAllSkippedCount }} {{ 'rows get this from a linked Shift and are skipped' | translate }}.
                            </p>
                          }
                          <div class="actions">
                            <button type="button" class="secondary" (click)="cancelApplyAll()">{{ 'Cancel' | translate }}</button>
                            <button type="button" class="primary" (click)="applyAll()">{{ 'Apply' | translate }}</button>
                          </div>
                        </div>
                      }
                    </div>
                  }
                  <div class="dialog-footer">
                    @if (activeStep === 1) {
                      <div class="footer-meta">
                        {{ matrixSegs.length }} {{ 'SEGs' | translate }} x {{ matrixOrgLevels.length }} {{ 'org levels shown' | translate }}
                      </div>
                    }
                    @if (activeStep === 2) {
                      <div class="footer-meta">
                        {{ filteredEntryRows.length }} {{ 'rows' | translate }}
                      </div>
                    }
                    <button type="button" class="secondary" (click)="cancel()">{{ 'Cancel' | translate }}</button>
                    @if (activeStep === 1) {
                      <button type="button" class="primary" [disabled]="isCreatingPairs" (click)="continueToGrid()">
                        @if (!isCreatingPairs) {
                          <span>{{ 'Create pairs and continue' | translate }}</span>
                        }
                        @if (isCreatingPairs) {
                          <span>{{ 'Creating pairs...' | translate }}</span>
                        }
                      </button>
                    }
                    @if (activeStep === 2) {
                      <button type="button" class="primary" (click)="done()">{{ 'Done' | translate }}</button>
                    }
                  </div>
                  <!-- Loading overlay during pair creation -->
                  @if (isCreatingPairs) {
                    <div class="loading-overlay">
                      <div class="loading-content">
                        <div class="spinner"></div>
                        <p>{{ 'Creating SEG and org level associations...' | translate }}</p>
                      </div>
                    </div>
                  }
                </div>
              } @else {
                <div class="loading-wrap">{{ 'Loading...' | translate }}</div>
              }

